// Canonical-library admin surface (C4.1 create + C4.2 edit/soft-delete/restore; PLAN-P9 D6).
// Wired through @sympllex/api-client + TanStack Query to the admin-worker (global libs, platform_admin-
// gated server-side). Library keys match the admin-worker registry. PLAN-P9 replaces the raw-JSON form
// with typed per-library forms (FIELD_SPECS) + a list search, keeping a raw-JSON "advanced" toggle so
// no column is un-editable. The 6 Owner-issue-9 libraries with tables are wired (Partner excluded).
// Built on the shadcn design system (visual parity with the tenant app).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Must match apps/admin-worker library registry keys (route = /admin/<key>).
const LIBRARIES = [
  "regulations", "legal-bases", "personal-data-categories", "data-subject-categories",
  "security-measures", "compliance-roles", "regulation-articles", "framework-kinds",
  "measure-categories", "improvement-points", "ticket-kinds",
  "methodology-documents", "validation-rules",
  // PLAN-P9 D6 — Owner-issue-9 libraries (Partner excluded: no canonical table, D5-gated).
  "functie", "ticket-types", "hoofddoelen", "bewijscontainer-templates", "policy-templates", "report-templates",
  // PLAN-P13 D13 — Art.46 transfer + Art.47 BCR reference data.
  "transfer-mechanisms", "adequacy-decisions", "art49-derogations", "bcrs",
] as const;

type FieldType = "text" | "textarea" | "number" | "bool" | "csv";
interface Field { key: string; label: string; type?: FieldType; required?: boolean }

// Typed field specs per library (required + key fields). Anything not listed is editable via the
// "Advanced (raw JSON)" toggle. uuid FKs are typed text for now (pickers are a later polish).
const FIELD_SPECS: Record<string, Field[]> = {
  regulations: [{ key: "jurisdictionId", label: "Jurisdiction id (uuid)", required: true }, { key: "source", label: "Source", required: true }, { key: "canonicalLanguage", label: "Canonical language", required: true }, { key: "shortCode", label: "Short code", required: true }, { key: "title", label: "Title", required: true }, { key: "longTitle", label: "Long title", type: "textarea" }],
  "legal-bases": [{ key: "regulationId", label: "Regulation id (uuid)", required: true }, { key: "kind", label: "Kind", required: true }, { key: "articleRef", label: "Article ref", required: true }, { key: "displayName", label: "Display name", required: true }],
  "personal-data-categories": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "kind", label: "Kind", required: true }],
  "data-subject-categories": [{ key: "shortCode", label: "Short code", required: true }, { key: "name", label: "Name", required: true }],
  "security-measures": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "description", label: "Description", type: "textarea" }],
  "compliance-roles": [{ key: "regulationId", label: "Regulation id (uuid)", required: true }, { key: "code", label: "Code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "roleKind", label: "Role kind", required: true }],
  "regulation-articles": [{ key: "regulationId", label: "Regulation id (uuid)", required: true }, { key: "articleRef", label: "Article ref", required: true }, { key: "title", label: "Title" }],
  "framework-kinds": [{ key: "code", label: "Code", required: true }, { key: "displayName", label: "Display name", required: true }],
  "measure-categories": [{ key: "code", label: "Code", required: true }, { key: "displayName", label: "Display name", required: true }],
  "improvement-points": [{ key: "code", label: "Code", required: true }, { key: "title", label: "Title", required: true }, { key: "description", label: "Description", type: "textarea" }],
  "ticket-kinds": [{ key: "code", label: "Code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "category", label: "Category", required: true }, { key: "stateMachineDefinition", label: "State-machine definition (JSON)", type: "textarea", required: true }],
  "methodology-documents": [{ key: "code", label: "Code", required: true }, { key: "versionLabel", label: "Version label", required: true }, { key: "title", label: "Title", required: true }, { key: "bodyMarkdown", label: "Body (markdown)", type: "textarea" }],
  "validation-rules": [{ key: "code", label: "Code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "description", label: "Description", type: "textarea" }],
  functie: [{ key: "shortCode", label: "Short code", required: true }, { key: "name", label: "Name", required: true }, { key: "description", label: "Description", type: "textarea" }],
  "ticket-types": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "description", label: "Description", type: "textarea" }, { key: "defaultGdprArticleAnchor", label: "Default GDPR article anchor" }],
  hoofddoelen: [{ key: "shortCode", label: "Short code", required: true }, { key: "name", label: "Name", required: true }, { key: "description", label: "Description", type: "textarea" }, { key: "category", label: "Category" }, { key: "applicableLawShortCodes", label: "Applicable law short codes (comma-sep)", type: "csv" }],
  "bewijscontainer-templates": [{ key: "shortCode", label: "Short code", required: true }, { key: "name", label: "Name", required: true }, { key: "description", label: "Description", type: "textarea" }, { key: "expectedEvidenceKinds", label: "Expected evidence kinds (comma-sep)", type: "csv" }, { key: "defaultAnchorKind", label: "Default anchor kind" }],
  "policy-templates": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "bodyMarkdown", label: "Body (markdown)", type: "textarea", required: true }, { key: "placeholderSchemaJson", label: "Placeholder schema (JSON)", type: "textarea" }],
  "report-templates": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "querySpecJson", label: "Query spec (JSON)", type: "textarea", required: true }, { key: "layoutSpecJson", label: "Layout spec (JSON)", type: "textarea", required: true }, { key: "defaultRenderTarget", label: "Default render target (pdf/html/…)" }, { key: "supportedRenderTargets", label: "Supported render targets (comma-sep)", type: "csv" }],
  "transfer-mechanisms": [{ key: "mechanismKind", label: "Mechanism kind (scc/bcr/adequacy/…)", required: true }, { key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "description", label: "Description", type: "textarea" }, { key: "sccModule", label: "SCC module" }],
  "adequacy-decisions": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "targetJurisdictionId", label: "Target jurisdiction id (uuid)", required: true }, { key: "euCommissionDecisionRef", label: "EU Commission decision ref" }, { key: "scope", label: "Scope" }],
  "art49-derogations": [{ key: "derogationKind", label: "Derogation kind (Art.49(1)(a)…)", required: true }, { key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "requiresSafeguardsDocumentation", label: "Requires safeguards documentation", type: "bool" }],
  "bcrs": [{ key: "shortCode", label: "Short code", required: true }, { key: "displayName", label: "Display name", required: true }, { key: "bcrType", label: "BCR type (controller/processor)", required: true }, { key: "description", label: "Description", type: "textarea" }],
};

const HIDDEN_EDIT_COLS = new Set([
  "id", "created_at", "created_by", "updated_at", "updated_by", "deleted_at",
  "is_active", "version", "content_set_id", "is_latest", "supersedes_id",
]);

type Row = Record<string, unknown>;

export function CanonicalLibrariesRoute() {
  const [library, setLibrary] = useState<string>(LIBRARIES[0]);
  const [editing, setEditing] = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["catalog", library] });

  const { data, isLoading, error, isFetching } = useQuery({ queryKey: ["catalog", library], queryFn: () => api.listCatalog(library) });
  const allRows = (data ?? []) as Row[];
  const needle = q.trim().toLowerCase();
  const rows = needle ? allRows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(needle))) : allRows;
  const colKeys = rows.length ? Object.keys(rows[0]!).filter((c) => c === "id" || !HIDDEN_EDIT_COLS.has(c) || c === "is_active").slice(0, 6) : [];

  const del = useMutation({ mutationFn: (id: string) => api.softDeleteCatalogRow(library, id), onSuccess: invalidate });
  const restore = useMutation({ mutationFn: (id: string) => api.restoreCatalogRow(library, id), onSuccess: invalidate });

  const columns: Column<Row>[] = colKeys.map((c) => ({
    key: c,
    header: c,
    render: (row) => {
      const active = row.is_active !== false;
      return <span className={active ? undefined : "opacity-50"}>{format(row[c])}</span>;
    },
  }));

  return (
    <section>
      <PageHeader
        title="Canonical libraries"
        description="Manage the platform's shared reference data."
        actions={
          <>
            {isFetching && <span className="text-sm text-muted-foreground">updating…</span>}
            <Can perm="super_tenant.canonical_library.write">
              <Button onClick={() => { setEditing(null); setCreating(true); }}>+ New row</Button>
            </Can>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-muted-foreground">Library</Label>
          <Select value={library} onValueChange={(v) => { setLibrary(v); setEditing(null); setCreating(false); setQ(""); }}>
            <SelectTrigger className="w-[280px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LIBRARIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {allRows.length > 0 && (
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this library…" aria-label="Search this library" className="w-[320px] max-w-full" />
        )}
      </div>

      {error && <p className="mb-4 text-sm text-destructive">Failed to load: {(error as Error).message}</p>}
      {!isLoading && !error && allRows.length > 0 && rows.length === 0 && <p className="mb-4 text-sm text-muted-foreground">No matches for &ldquo;{q}&rdquo;.</p>}

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(row) => String(row.id ?? Math.random())}
        empty="No rows in this library yet."
        rowActions={(row) => {
          const id = row.id as string;
          const active = row.is_active !== false;
          return (
            <Can perm="super_tenant.canonical_library.write" fallback={<span className="text-muted-foreground">—</span>}>
              <div className="flex justify-end gap-1.5">
                <Button size="xs" variant="outline" onClick={() => { setCreating(false); setEditing(row); }}>Edit</Button>
                {active
                  ? <Button size="xs" variant="destructive" disabled={del.isPending} onClick={() => del.mutate(id)}>Delete</Button>
                  : <Button size="xs" variant="outline" disabled={restore.isPending} onClick={() => restore.mutate(id)}>Restore</Button>}
              </div>
            </Can>
          );
        }}
      />
      {(del.isError || restore.isError) && <p className="mt-2 text-sm text-destructive">{errMsg((del.error ?? restore.error) as unknown)}</p>}

      <Can perm="super_tenant.canonical_library.write">
        <RowDialog
          key={editing ? (editing.id as string) : creating ? "new" : "closed"}
          open={creating || editing !== null}
          library={library}
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); invalidate(); }}
        />
      </Can>
    </section>
  );
}

function buildPayload(fields: Field[], state: Record<string, string | boolean>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = state[f.key];
    if (f.type === "bool") { out[f.key] = raw === true || raw === "true"; continue; }
    const s = typeof raw === "string" ? raw.trim() : "";
    if (!s) continue;
    if (f.type === "number") out[f.key] = Number(s);
    else if (f.type === "csv") out[f.key] = s.split(",").map((x) => x.trim()).filter(Boolean);
    else out[f.key] = s;
  }
  return out;
}
function missingRequired(fields: Field[], state: Record<string, string | boolean>): boolean {
  return fields.some((f) => f.required && f.type !== "bool" && !String(state[f.key] ?? "").trim());
}

function RowDialog({ open, library, editing, onClose, onDone }: { open: boolean; library: string; editing: Row | null; onClose: () => void; onDone: () => void }) {
  const isEdit = editing !== null;
  const fields = FIELD_SPECS[library] ?? [];
  const [advanced, setAdvanced] = useState(fields.length === 0); // no spec → raw JSON only
  const initialState: Record<string, string | boolean> = {};
  for (const f of fields) {
    const v = isEdit ? editing![f.key] : undefined;
    initialState[f.key] = f.type === "bool" ? Boolean(v) : Array.isArray(v) ? (v as unknown[]).join(", ") : v != null ? String(v) : "";
  }
  const [state, setState] = useState(initialState);
  const set = (k: string, v: string | boolean) => setState((s) => ({ ...s, [k]: v }));
  const [body, setBody] = useState(isEdit ? prettyEditable(editing!) : "{\n  \n}");
  const [parseError, setParseError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => isEdit ? api.updateCatalogRow(library, editing!.id as string, payload) : api.createCatalogRow(library, payload),
    onSuccess: onDone,
  });

  const submit = () => {
    setParseError(null);
    let payload: Record<string, unknown>;
    if (advanced) { try { payload = JSON.parse(body); } catch (e) { setParseError("Invalid JSON: " + (e as Error).message); return; } }
    else payload = buildPayload(fields, state);
    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { mutation.reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle>{isEdit ? `Edit ${library} row` : `New ${library} row`}</DialogTitle>
            {fields.length > 0 && (
              <Button size="xs" variant="ghost" onClick={() => setAdvanced((a) => !a)}>{advanced ? "Typed form" : "Advanced (raw JSON)"}</Button>
            )}
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {advanced ? (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} spellCheck={false}
              className="w-full rounded-2xl border border-transparent bg-input/50 p-3 font-mono text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30" />
          ) : (
            fields.map((f) => (
              <div key={f.key} className="flex flex-col gap-1.5">
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "textarea" ? (
                  <textarea value={String(state[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} rows={3}
                    className="w-full rounded-2xl border border-transparent bg-input/50 px-2.5 py-1.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30" />
                ) : f.type === "bool" ? (
                  <Checkbox checked={state[f.key] === true} onCheckedChange={(c) => set(f.key, c === true)} />
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} value={String(state[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} />
                )}
              </div>
            ))
          )}
        </div>
        {parseError && <p className="text-sm text-destructive">{parseError}</p>}
        {mutation.isError && <p className="text-sm text-destructive">{errMsg(mutation.error as unknown)}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => { mutation.reset(); onClose(); }}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending || (!advanced && missingRequired(fields, state))}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function format(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
function prettyEditable(row: Record<string, unknown>): string {
  const editable: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) if (!HIDDEN_EDIT_COLS.has(k)) editable[k] = v;
  return JSON.stringify(editable, null, 2);
}
function errMsg(e: unknown): string {
  return e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;
}
